import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { Check, ChevronDown, ChevronUp, Circle, CircleCheck, CircleX, Clock, Info, Loader2, Pill, Plus, X } from "lucide-react";
// import { useSendInvoiceMutation } from "../api/mutations";
import { toast } from "sonner";

import { Badge } from "@workspace/web-ui/components/badge";
import { Button } from "@workspace/web-ui/components/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@workspace/web-ui/components/dialog";
import { Image } from "@workspace/web-ui/components/image";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@workspace/web-ui/components/select";
import { cn } from "@workspace/web-ui/lib/utils";
import {
    useApproveRefillMutation,
    useRejectRefillMutation
} from "~/features/dashboard/api/refills";
import { MedicalRefillsComponent } from "~/features/dashboard/components/refills";
import {
    ImagesDocsSection,
    ImagesDocsSectionWhite,
} from "~/features/dashboard/components/refills/images-docs-section";
import { PatientCard } from "~/features/dashboard/components/refills/patient-card";
import { formatDate } from "~/utils";
import { PrescriptionModal } from "../../patient-logs/components/modals/prescription-modal";
import DosespotButton from "../../patient-logs/pages/dosespotModal";
import { useSendInvoiceMutation } from "../api/mutations";
import { ResolveToggle } from "~/features/dashboard/components/resolve-toggle";
import { FormattedEncounter } from "../types";
import { getRefillsByIdOptions } from "../api/queries/refills";

interface ImageModalProps {
    isOpen: boolean;
    onClose: () => void;
    imageUrl: string;
    imageIndex: number;
    totalImages: number;
}

const ImageModal = ({ isOpen, onClose, imageUrl, imageIndex, totalImages }: ImageModalProps) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-h-[90vh] w-full max-w-4xl p-0 xl:max-h-[95vh]">
                <DialogHeader className="p-6 pb-0">
                    <DialogTitle className="text-lg font-semibold">
                        Image {imageIndex + 1} of {totalImages}
                    </DialogTitle>
                </DialogHeader>
                <div className="p-6 pt-4">
                    <div className="relative">
                        <Image
                            src={imageUrl}
                            alt={`Encounter Image ${imageIndex + 1}`}
                            className="h-auto max-h-[70vh] w-full rounded-md object-contain"
                            layout="constrained"
                            width={96}
                            height={96}
                        />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const RequestInfo = ({
    request_info,
}: {
    request_info: { date_submitted: string; last_refill: string; days_supply: string };
}) => {
    return (
        <div className="grid w-full grid-cols-[140px_1fr] gap-y-2">
            <p className="text-sm font-normal text-[#202020CC]">Request Type:</p>
            <p className="text-sm font-normal text-[#202020CC]">Medication Refill</p>

            <p className="text-sm font-normal text-[#202020CC]">Submitted:</p>
            <p className="text-sm font-normal text-[#202020CC]">
                {formatDate(request_info.date_submitted, "datetime")}
            </p>

            <p className="text-sm font-normal text-[#202020CC]">Last Refill:</p>
            <p className="text-sm font-normal text-[#202020CC]">{formatDate(request_info.last_refill)}</p>

            <p className="text-sm font-normal text-[#202020CC]">Days Supply:</p>
            <p className="text-sm font-normal text-[#202020CC]">{request_info.days_supply} days</p>
        </div>
    );
};

const RequestInfoUrgentCare = ({
    request_info,
}: {
    request_info: {
        date_submitted: string;
        last_refill: string;
        days_supply: string;
        medication: any;
        patient: any;
        refill: any;
    };
}) => {
    return (
        <div className="grid w-full grid-cols-[100px_1fr] gap-y-2">
            <p className="text-sm font-semibold text-[#202020CC]">Medication:</p>
            <p className="text-sm font-normal text-[#202020CC]">{request_info.medication}</p>

            <p className="text-sm font-semibold text-[#202020CC]">Dosage:</p>
            <p className="text-sm font-normal text-[#202020CC]">{request_info.patient}</p>

            <p className="text-sm font-semibold text-[#202020CC]">Refill:</p>
            <p className="text-sm font-normal text-[#202020CC]">{request_info.refill ?? 0}</p>
        </div>
    );
};

export const EncounterMedicationRefillDetails = ({ encounter }: { encounter: FormattedEncounter }) => {
    console.log("encounter", encounter);
    const { data: medication, isLoading, isError } = useQuery(getRefillsByIdOptions({
        id: encounter.id ?? ""
    }))
    console.log("medication", medication);
    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();

    // Debug: Log recommendedTasks
    console.log("🔍 Recommended Tasks in refill-details:", encounter?.recommendedTasks);
    console.log("🔍 Encounter object:", encounter);
    console.log("🔍 Program:", program);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<{ url: string; index: number } | null>(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectDetails, setRejectDetails] = useState("");
    const [reasonError, setReasonError] = useState("");
    const [expandedRefills, setExpandedRefills] = useState<Set<string>>(new Set());
    const [rejectingRefillId, setRejectingRefillId] = useState<string | null>(null);
    const [rejectingMedication, setRejectingMedication] = useState<{ name: string; dosage: string } | null>(null);
    const [openPrescriptionModal, setOpenPrescriptionModal] = useState(false);

    const approveRefillMutation = useApproveRefillMutation({
        refill_id: encounter.id ?? "",
    });

    const rejectRefillMutation = useRejectRefillMutation();

    const sendInvoiceMutation = useSendInvoiceMutation();

    // const handleApproveRefill = async () => {
    //     try {
    //         const response = await approveRefillMutation.mutateAsync();

    //         if (response.status === "success") {
    //             toast.success('Refill sent to pharmacy successfully', {
    //                 style: {
    //                     background: "#10B981",
    //                     color: "#FFFFFF",
    //                     borderColor: "#10B981",
    //                 } as React.CSSProperties
    //             });
    //         }
    //     } catch (error) {
    //         console.error("Error sending refill to pharmacy:", error);
    //         toast.error('Failed to send refill to pharmacy.');
    //     }
    // };

    const handleApproveRefill = async () => {
        console.log("clicked", medication?.status);
        try {
            // const response = await approveRefillMutation.mutateAsync();

            if (medication?.status === "approved") {
                console.log("success");
                // If it's weight-loss program and user has paid for the refill, send invoice to pharmacy
                if (program === "weight-loss" && medication?.paymentStatus === "paid") {
                    try {
                        await sendInvoiceMutation.mutateAsync({
                            patient_id: medication?.patient.user_id,
                            type_id: medication?.id,
                            type: "Refill",
                        });
                    } catch (invoiceError) {
                        // Don't fail the whole operation if invoice sending fails
                    }
                }

                toast.success("Refill Sent to Pharmacy Successfully", {
                    style: {
                        background: "#10B981",
                        color: "#FFFFFF",
                        borderColor: "#10B981",
                        "& svg": {
                            color: "#FFFFFF",
                        },
                    } as React.CSSProperties,
                });
            }
        } catch (error) {
            return toast.error("Failed to approve refill request.", {
                style: {
                    background: "#DC2626",
                    color: "#FFFFFF",
                    borderColor: "#DC2626",
                    "& svg": {
                        color: "#FFFFFF",
                    },
                } as React.CSSProperties,
            });
        }
    };

    const handleRejectRefill = async (reason: string, details: string, specificRefillId?: string) => {
        try {
            const targetRefillId = specificRefillId ?? encounter.id ?? "";
            const response = await rejectRefillMutation.mutateAsync({
                refill_id: targetRefillId,
                reason,
                details,
            });

            if (response.status === "success") {
                toast.success('Refill Request Rejected Successfully', {
                    style: {
                        background: "#10B981",
                        color: "#FFFFFF",
                        borderColor: "#10B981",
                    } as React.CSSProperties
                });

                // If not rejecting a specific refill, close the modal
                if (!specificRefillId) {
                    setShowRejectModal(false);
                }
            }
        } catch (error) {
            console.error("Error rejecting refill request:", error);
            toast.error('Failed to reject refill request.');
        }
    };

    const toggleRefillExpansion = (refillId: string) => {
        setExpandedRefills((prev) => {
            // If clicking on an already expanded refill, close it
            if (prev.has(refillId)) {
                return new Set();
            }
            // Otherwise, close all others and open only this one (accordion behavior)
            return new Set([refillId]);
        });
    };

    const openRejectModal = (refillId?: string, medication?: { name: string; dosage: string }) => {
        setRejectingRefillId(refillId || null);
        setRejectingMedication(medication || null);
        setShowRejectModal(true);
    };

    const closeRejectModal = () => {
        setShowRejectModal(false);
        setRejectingRefillId(null);
        setRejectingMedication(null);
        setRejectReason("");
        setRejectDetails("");
        setReasonError("");
    };

    const handleImageClick = (imageUrl: string, index: number) => {
        setSelectedImage({ url: imageUrl, index });
    };

    const handleCloseImageModal = () => {
        setSelectedImage(null);
    };

    const handleAddMedication = () => {
        setOpenPrescriptionModal(true);
    };

    // Determine if "Send to Pharmacy" button should be disabled based on tasks
    const shouldDisableSendToPharmacy = () => {
        const tasks = encounter?.recommendedTasks ?? [];

        // If there are no tasks, button should be enabled
        if (!Array.isArray(tasks) || tasks.length > 0) {
            return false;
        }

        // If there are tasks, check if any are completed (paid for)
        const hasCompletedTask = tasks.some((task: any) => task?.status === "completed");

        // If all tasks are pending (none completed), disable the button
        // If at least one task is completed, enable the button
        return !hasCompletedTask;
    };

    // Check if any task has is_erx_sent === true to determine button text
    const shouldShowResendText = () => {
        const tasks = encounter?.recommendedTasks ?? [];
        return (
            Array.isArray(tasks) &&
            tasks.length > 0 &&
            tasks.some((task: any) => task?.is_erx_sent === true)
        );
    };

    if (isLoading) {
        return (
            <Loader2 className="flex animate-spin items-center justify-center text-blue-700 duration-1000" />
        );
    }

    if (isError) {
        console.log("error", isError);
        return null;
    }

    // const handleApprove = () => {
    //     console.log("Approved!");
    // };

    // const handleReject = () => {
    //     console.log("Rejected!");
    // };

    return (
        <>
            {program === "weight-loss" && (
                <div className="flex flex-col gap-4 ml-4 h-full bg-[#FFFFFF] rounded-[24px] border border-[#0404041A] p-4 sm:py-[16px] sm:px-[32px]">
                    <div className="flex items-center gap-2">
                        <span className="border-[#00C78133] text-[#00C781] text-xs font-medium py-[2px] px-[6px] rounded-[888px] border bg-[#E6FAF2] w-fit flex gap-1">
                            <Pill className="w-3.5 h-3.5 text-[#00C781]" />
                            Refill
                        </span>
                        {(() => {
                            const tasks = encounter?.recommendedTasks ?? [];

                            // If rejected, only show rejected tag, no task status tags
                            if (medication?.status === "rejected") {
                                return (
                                    <span className="ml-2 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-600 border border-red-200">
                                        Rejected
                                    </span>
                                );
                            }

                            // Refill status tags (approved/fulfilled) - shown separately
                            const refillStatusTag = (() => {
                                if (medication?.status && medication.status !== "requested") {
                                    if (medication.status === "approved") {
                                        return null
                                    }

                                    return (
                                        <span
                                            key="fulfilled"
                                            className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${medication.status === "fulfilled"
                                                ? "bg-blue-100 text-blue-700 border border-blue-200"
                                                : medication.status === "partially_fulfilled"
                                                    ? "bg-yellow-100 text-yellow-700 border border-yellow-200"
                                                    : "bg-gray-100 text-gray-500 border border-gray-200"
                                                }`}
                                        >
                                            {medication.status === "fulfilled"
                                                ? "Fulfilled"
                                                : medication.status === "partially_fulfilled"
                                                    ? "Partially Fulfilled"
                                                    : medication.status?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                                        </span>
                                    );
                                }
                                return null;
                            })();

                            // Task status tags (in progress/action needed) - shown separately
                            const taskStatusTag = (() => {
                                const hasTasks = Array.isArray(tasks) && tasks.length > 0;
                                const hasPendingTask = hasTasks && tasks.some((task: any) => task?.status === "pending");
                                const isErxSent = hasTasks && tasks.some((task: any) => task?.is_erx_sent === true);

                                let statusToShow = "Action needed";
                                let statusClasses = "text-[#FFA726] bg-[#FFF5E6]";
                                let Icon = null;

                                if (hasPendingTask) {
                                    statusToShow = "In progress";
                                    statusClasses = "text-[#2563EB] bg-[#EFF6FF]";
                                    Icon = Clock;
                                } else if (isErxSent) {
                                    statusToShow = "Resolved";
                                    statusClasses = "text-[#00C781] bg-[#E6FAF2]";
                                    Icon = CircleCheck;
                                }

                                return (
                                    <span key="task-status" className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${statusClasses} flex items-center gap-1`}>
                                        {Icon && <Icon className="w-3 h-3" />}
                                        {statusToShow}
                                    </span>
                                );
                            })();

                            // Return both tags if they exist
                            return (
                                <>
                                    {refillStatusTag}
                                    {taskStatusTag}
                                </>
                            );
                        })()}
                    </div>
                    <ResolveToggle
                        encounterId={encounter.encounterId}
                        recommendedTasks={encounter.recommendedTasks}
                        disabled={!(Array.isArray(encounter.recommendedTasks) && encounter.recommendedTasks.length > 0)}
                        disabledReason="No medication available"
                    />

                    <div className="mt-1 flex flex-row items-center justify-between gap-2">
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-row items-center gap-2">
                                <h2 className="text-3xl font-bold">{medication?.userName}</h2>
                                {encounter.patient.medical_plan?.payment_status === "paid" &&
                                    encounter.patient.glp1_guide_accepted &&
                                    encounter.patient.identity_verified &&
                                    encounter.patient.supplementary_health_assessment &&
                                    Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                                        <div className="">
                                            <Image
                                                src="/icons/check.svg"
                                                alt="timeline"
                                                layout="constrained"
                                                className="h-4 w-4 sm:h-5 sm:w-5"
                                                width={96}
                                                height={96}
                                            />
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>

                    {/* Patient Information Card */}
                    <PatientCard
                        patientEmail={encounter.patient.email ?? ""}
                        patientName={encounter.patient.name ?? ""}
                        programEnrollment="Weight Loss"
                        requestDate={encounter?.createdAt || ""}
                        currentWeight=""
                        patientId={encounter.patient.id}
                    />

                    {/* Medication Refill Request Information */}
                    <div className="">
                        <div className="flex flex-col items-start gap-4 rounded-lg border border-[#E6E6E6] p-2">
                            <h3 className="text-lg font-medium">Request Information</h3>
                            <RequestInfo
                                request_info={{
                                    date_submitted: medication?.requestedDate ?? "",
                                    last_refill: medication?.requestedDate ?? "",
                                    days_supply: "30",
                                }}
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-6 w-full items-center">
                            <Button
                                className="w-[133px] !bg-[#E12121] !py-[12px] px-[16px] h-[44px] rounded"
                                onClick={() => openRejectModal()}
                            >
                                Reject Refill
                            </Button>
                            <div className="flex-1">
                                <Button
                                    className="!text-white !rounded-[8px] !border-none w-full h-[44px]"
                                    onClick={handleApproveRefill}
                                    disabled={approveRefillMutation.isPending || shouldDisableSendToPharmacy()}
                                >
                                    {approveRefillMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                                    ) : (
                                        shouldShowResendText() ? "Resend to Pharmacy" : "Send to Pharmacy"
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Reject Modal */}
                        <Dialog open={showRejectModal} onOpenChange={closeRejectModal}>
                            <DialogContent className="rounded-[24px]">
                                <DialogHeader className="flex items-center justify-center">
                                    <DialogTitle className="text-xl font-medium">Reject Request</DialogTitle>
                                </DialogHeader>
                                <div>
                                    <p className="mb-4 flex items-center justify-center text-center text-xl font-medium">
                                        You are rejecting the refill request for {medication?.userName}.
                                    </p>
                                    <label className="mt-4 text-[14px] text-[#1A202C]">
                                        <Select value={rejectReason} onValueChange={setRejectReason}>
                                            <SelectTrigger className="mb-4 mt-2 h-[44px] w-full rounded-[8px] border p-2">
                                                <SelectValue placeholder="Select a reason" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectGroup>
                                                    <SelectItem value="Cannot prescribe this medication">
                                                        Cannot prescribe this medication
                                                    </SelectItem>
                                                    <SelectItem value="Medication Completed / Discontinued">
                                                        Medication Completed / Discontinued
                                                    </SelectItem>
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                        <label className="mt-4 text-[14px] text-[#1A202C]">
                                            Please provide additional details:
                                        </label>
                                        <textarea
                                            value={rejectDetails}
                                            onChange={(e) => setRejectDetails(e.target.value)}
                                            maxLength={200}
                                            className="mt-2 h-[164px] w-full rounded-[8px] border p-2"
                                        />
                                    </label>
                                    {reasonError && <div className="text-red-500">{reasonError}</div>}
                                    <div className="mt-4 flex justify-end gap-2">
                                        <Button
                                            onClick={closeRejectModal}
                                            className="rounded-[8px] border border-[#DCDCDE] bg-transparent text-[#202020CC] hover:bg-transparent"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            className="rounded-[8px] !bg-[#E12121] text-white"
                                            disabled={rejectRefillMutation.isPending}
                                            onClick={async () => {
                                                if (!rejectReason.trim()) {
                                                    setReasonError("Reason is required.");
                                                    return;
                                                }
                                                if (!rejectDetails.trim()) {
                                                    setReasonError("Additional details are required.");
                                                    return;
                                                }
                                                setReasonError("");
                                                await handleRejectRefill(
                                                    rejectReason,
                                                    rejectDetails,
                                                    rejectingRefillId || undefined
                                                );
                                                closeRejectModal();
                                            }}
                                        >
                                            {rejectRefillMutation.isPending ? (
                                                <Loader2 className="h-4 w-4 animate-spin text-white" />
                                            ) : (
                                                "Confirm"
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>

                    {/* Side Effects Section */}
                    <div className="mt-6 w-full">
                        <div className="flex min-h-[200px] flex-col rounded-lg bg-gray-50 p-4">
                            <h3 className="mb-4 text-lg font-semibold">Side Effects</h3>
                            {medication?.sideEffects ? (
                                <div className="flex-1 space-y-3">
                                    {(() => {
                                        const sideEffects = medication?.sideEffects as Record<string, any>;
                                        const reportedEffects: string[] = [];

                                        // Map of side effect keys to display names
                                        const effectLabels: Record<string, string> = {
                                            nausea: "Nausea",
                                            diarrhea: "Diarrhea",
                                            constipation: "Constipation",
                                            headache: "Headache",
                                            none: "None",
                                        };

                                        // Collect all true side effects
                                        Object.keys(effectLabels).forEach((key) => {
                                            if (sideEffects[key] === true && effectLabels[key]) {
                                                reportedEffects.push(effectLabels[key]);
                                            }
                                        });

                                        const hasReportedEffects = reportedEffects.length > 0;
                                        const hasOtherSymptoms =
                                            medication?.sideEffects?.other_symptoms &&
                                            medication?.sideEffects?.other_symptoms.trim() !== "";

                                        if (!hasReportedEffects && !hasOtherSymptoms) {
                                            return (
                                                <div className="flex flex-1 flex-col items-center justify-center py-6 text-gray-400">
                                                    <span className="mb-2 text-3xl">📋</span>
                                                    <span className="px-2 text-center text-sm">No side effects reported</span>
                                                </div>
                                            );
                                        }

                                        return (
                                            <>
                                                {hasReportedEffects && (
                                                    <div>
                                                        <p className="mb-2 text-sm font-medium text-gray-700">
                                                            Reported Symptoms:
                                                        </p>
                                                        <div className="flex flex-wrap gap-2 rounded-md border border-gray-200 bg-white p-1">
                                                            {reportedEffects.map((effect, idx) => (
                                                                <li
                                                                    key={idx}
                                                                    className="list-inside list-disc rounded-full px-3 py-1.5 text-sm"
                                                                    style={{ display: "list-item" }}
                                                                >
                                                                    {effect}
                                                                </li>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {hasOtherSymptoms && (
                                                    <div>
                                                        <p className="mb-2 text-sm font-medium text-gray-700">
                                                            Additional Symptoms:
                                                        </p>
                                                        <div className="rounded-md border border-gray-200 bg-white px-4 py-2">
                                                            <ul className="list-inside list-disc space-y-1">
                                                                {sideEffects.other_symptoms
                                                                    .split("\n")
                                                                    .map((symptom: string, idx: number) => {
                                                                        const trimmed = symptom.trim();
                                                                        if (!trimmed) return null;
                                                                        return (
                                                                            <li key={idx}>
                                                                                <span className="whitespace-pre-wrap text-sm text-gray-700">
                                                                                    {trimmed}
                                                                                </span>
                                                                            </li>
                                                                        );
                                                                    })}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            ) : (
                                <div className="flex flex-1 flex-col items-center justify-center py-6 text-gray-400">
                                    <span className="mb-2 text-3xl">📋</span>
                                    <span className="px-2 text-center text-sm">No side effects reported</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recommended Actions Section for weight-loss */}
                    {(() => {
                        const tasks = encounter?.recommendedTasks ?? [];
                        const hasAssignedActions = Array.isArray(tasks) && tasks.length > 0;
                        const isRejected = medication?.status === "rejected";

                        // Debug: Log tasks
                        console.log("🔍 Tasks in recommended actions section:", tasks);
                        console.log("🔍 hasAssignedActions:", hasAssignedActions);
                        console.log("🔍 isRejected:", isRejected);
                        console.log("🔍 encounter?.recommendedTasks:", encounter?.recommendedTasks);
                        console.log("🔍 Section is rendering for weight-loss!");

                        const humanize = (s?: string) =>
                            s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Task";

                        const getTaskTitle = (task: any) => {
                            if (task?.type === "medication_payment") return "Prescribed Medication";
                            if (task?.type === "appointment_followup") return "Pay for Appointment";
                            return humanize(task?.type);
                        };

                        const formatDate = (d?: string | number) => {
                            if (!d) return "-";
                            const dt = new Date(d);
                            if (isNaN(dt.getTime())) return "-";
                            return dt.toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            });
                        };

                        return (
                            <div className="flex flex-col gap-3 w-full text-left rounded-[16px] border border-[#0404041A] pb-4 px-4 pt-4 mt-6">
                                <div className="flex items-center justify-between w-full">
                                    <h2 className="font-medium text-[#1A202C]">
                                        Recommended actions
                                    </h2>
                                    <Button
                                        className="text-white bg-[#4272DD] w-fit rounded-[8px] shadow-none border border-none hover:bg-[#4272DD]"
                                        onClick={handleAddMedication}
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        <p>Add medication</p>
                                    </Button>
                                </div>

                                {hasAssignedActions || isRejected ? (
                                    <div className="space-y-4">
                                        {/* Show rejected refill as a recommended action */}
                                        {isRejected && medication?.rejectionReason && (
                                            <div className="rounded-[8px] bg-[#F7F8FA] p-4">
                                                <div className="mb-3 flex items-start justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500">
                                                            <X className="h-3 w-3 text-white" />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-medium text-red-600">Rejected Refill Request</h3>
                                                            <p className="text-sm text-[#6B7280]">
                                                                Rejected: {formatDate(medication?.requestedDate)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="space-y-2 rounded-md bg-white p-2">
                                                    <h4 className="font-medium text-[#1A202C]">
                                                        {medication?.rejectionReason?.reason || "Rejection Reason"}
                                                    </h4>
                                                    <div className="grid grid-cols-1 gap-4 text-sm text-[#6B7280] md:grid-cols-2">
                                                        {medication?.isBatch && Array.isArray(medication?.medications) ? (
                                                            // Show all medications for batch
                                                            medication.medications.map((med: any, idx: number) => (
                                                                <div key={idx} className="mb-2 border-b pb-2 md:col-span-2">
                                                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                                                        <div>
                                                                            <span className="font-medium">Medication: </span>
                                                                            <span className="text-red-600 line-through">
                                                                                {med?.name ?? "-"}
                                                                            </span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Dosage: </span>
                                                                            {med?.dosage ?? "-"}
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Refills: </span>
                                                                            {med?.refills ?? "-"}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <>
                                                                <div>
                                                                    <span className="font-medium">Medication: </span>
                                                                    <span className="text-red-600 line-through">
                                                                        {!Array.isArray(medication?.medications)
                                                                            ? (medication?.medications?.medication ?? "-")
                                                                            : "-"}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Dosage: </span>
                                                                    {!Array.isArray(medication?.medications)
                                                                        ? (medication?.medications?.medicationDosage ?? "-")
                                                                        : "-"}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Refills: </span>
                                                                    {!Array.isArray(medication?.medications)
                                                                        ? (medication?.medications?.refills ?? "-")
                                                                        : "-"}
                                                                </div>
                                                            </>
                                                        )}
                                                        <div className="md:col-span-2">
                                                            <span className="font-medium">Rejection Details: </span>
                                                            <span>{medication?.rejectionReason?.details ?? "-"}</span>
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Request Date: </span>
                                                            {formatDate(medication?.requestedDate)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Show recommended tasks */}
                                        {tasks.map((task: any, index: number) => {
                                            const isMedicationTask = task?.type === "medication_payment";
                                            const meds = task?.prescription?.medication ?? [];
                                            const isTaskCompleted = task?.status === "completed";
                                            const isTaskPending = task?.status === "pending";
                                            const hasPrescription = task?.prescription && meds.length > 0;
                                            // Only show green if task is completed (not pending) and has prescription for medication tasks
                                            const showGreen = isMedicationTask
                                                ? (hasPrescription && !isTaskPending && isTaskCompleted)
                                                : isTaskCompleted;

                                            return (
                                                <div
                                                    key={task?.task_id || index}
                                                    className="rounded-[8px] bg-[#F7F8FA] p-4"
                                                >
                                                    <div className="mb-3 flex items-start justify-between">
                                                        <div className="flex items-center gap-3">
                                                            {showGreen ? (
                                                                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500">
                                                                    <Check className="h-3 w-3 text-white" />
                                                                </div>
                                                            ) : (
                                                                <Circle className="h-5 w-5 text-blue-500" />
                                                            )}
                                                            <div>
                                                                <h3
                                                                    className={cn(
                                                                        "font-medium",
                                                                        showGreen ? "text-green-600 line-through" : "text-[#1A202C]"
                                                                    )}
                                                                >
                                                                    {getTaskTitle(task)}
                                                                </h3>
                                                                <p className="text-sm text-[#6B7280]">
                                                                    Assigned: {formatDate(task?.created_at)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Medication details */}
                                                    {isMedicationTask && Array.isArray(meds) && meds.length > 0 && (
                                                        <div className="space-y-3">
                                                            {meds.map((m: any, i: number) => (
                                                                <div
                                                                    key={`${m?.name || "med"}-${i}`}
                                                                    className="space-y-2 rounded-md bg-white p-2"
                                                                >
                                                                    <h4 className="font-medium text-[#1A202C]">{m?.name || "-"}</h4>
                                                                    <div className="grid grid-cols-1 gap-4 text-sm text-[#6B7280] md:grid-cols-2">
                                                                        <div>
                                                                            <span className="font-medium">Dosage: </span>
                                                                            {m?.dosage ?? "-"}
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Duration: </span>
                                                                            {typeof m?.duration === "number"
                                                                                ? `${m.duration} days`
                                                                                : (m?.duration ?? "-")}
                                                                        </div>
                                                                        <div className="md:col-span-2">
                                                                            <span className="font-medium">Instructions: </span>
                                                                            {Array.isArray(m?.instructions) ? (
                                                                                <ul className="ml-5 list-disc">
                                                                                    {m.instructions.map((ins: string, idx: number) => (
                                                                                        <li key={idx}>{ins}</li>
                                                                                    ))}
                                                                                </ul>
                                                                            ) : (
                                                                                <span>{m?.instructions ?? "-"}</span>
                                                                            )}
                                                                        </div>
                                                                        {/* <div>
                                                                            <span className="font-medium">Refills: </span>
                                                                            {m?.refills ?? "-"}
                                                                        </div> */}
                                                                        <div>
                                                                            <span className="font-medium">Effective date: </span>
                                                                            {formatDate(m?.effective_date)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Non-medication task details */}
                                                    {!isMedicationTask && (
                                                        <div className="space-y-2 rounded-md bg-white p-2 text-sm text-[#6B7280]">
                                                            <div>
                                                                <span className="font-medium">Price: </span>
                                                                <span>{task?.cost ?? "-"}</span>
                                                            </div>
                                                            {task?.instructions && (
                                                                <div>
                                                                    <span className="font-medium">Instructions: </span>
                                                                    <span>{task.instructions}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center rounded-[8px] bg-[#F7F8FA] px-6 py-12 text-center">
                                        <p className="mb-1 font-medium text-[#1A202C]">No recommended actions</p>
                                        <p className="text-sm text-[#6B7280]">
                                            When actions are recommended, they will appear here.
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            )}

            {program === "urgent-care" && (
                <div className="flex flex-col gap-4 ml-4 h-full bg-[#FFFFFF] rounded-[24px] border border-[#0404041A] p-4 sm:py-[16px] sm:px-[32px]">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center">
                        <span className="border-[#00C78133] text-[#00C781] text-xs font-medium py-[2px] px-[6px] rounded-[888px] border bg-[#E6FAF2] w-fit flex gap-1">
                            <Pill className="w-3.5 h-3.5 text-[#00C781]" />
                            Refill
                        </span>
                        {/* Add status tag display for refill request */}
                        {medication?.status && (
                            <span
                                className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${medication.status === "rejected"
                                    ? "bg-red-100 text-red-600 border border-red-200"
                                    : medication.status === "requested"
                                        ? "bg-[#FFF5E6] text-[#FFA726]"
                                        : medication.status === "fulfilled"
                                            ? "bg-blue-100 text-blue-700 border border-blue-200"
                                            : medication.status === "partially_fulfilled"
                                                ? "bg-yellow-100 text-yellow-700 border border-yellow-200"
                                                : medication.status === "approved"
                                                    ? "bg-[#E6FAF2] text-[#00C781] border border-green-200"
                                                    : "bg-gray-100 text-gray-500 border border-gray-200"
                                    }`}
                            >
                                {/* icons */}
                                {medication.status === "rejected" && <CircleX className="w-3.5 h-3.5 text-[#DC2626]" />}
                                {medication.status === "requested" && <Info className="w-3.5 h-3.5 text-[#FFA726]" />}
                                {medication.status === "fulfilled" && <CircleCheck className="w-3.5 h-3.5 text-[#00C781]" />}
                                {medication.status === "partially_fulfilled" && <CircleCheck className="w-3.5 h-3.5 text-[#00C781]" />}
                                {medication.status === "approved" && <CircleCheck className="w-3.5 h-3.5 text-[#00C781]" />}
                                {medication.status === "rejected"
                                    ? "Rejected"
                                    : medication.status === "requested"
                                        ? "Action Needed"
                                        : medication.status === "fulfilled"
                                            ? "Fulfilled"
                                            : medication.status === "partially_fulfilled"
                                                ? "Partially Fulfilled"
                                                : medication.status === "approved"
                                                    ? "Resolved"
                                                    : medication.status?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                        )}
                        </div>
                        <ResolveToggle encounterId={encounter.encounterId} recommendedTasks={encounter.recommendedTasks} />
                    </div>
                    {/* <h2 className="text-xl font-medium">Medication Refill Request</h2> */}

                    <div className="mt-1 flex flex-row items-center justify-between gap-2">
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-row items-center gap-2">
                                <h2 className="text-3xl font-bold">{medication?.userName}</h2>
                                {program === "urgent-care"
                                    ? encounter.patient.urgent_care_onboarded &&
                                    encounter.patient.identity_verified && (
                                        <div className="">
                                            <Image
                                                src="/icons/check.svg"
                                                alt="timeline"
                                                layout="constrained"
                                                className="h-4 w-4 sm:h-5 sm:w-5"
                                                width={96}
                                                height={96}
                                            />
                                        </div>
                                    )
                                    : encounter.patient.medical_plan?.payment_status === "paid" &&
                                    encounter.patient.glp1_guide_accepted &&
                                    encounter.patient?.supplementary_health_assessment && (
                                        <div className="">
                                            <Image
                                                src="/icons/check.svg"
                                                alt="timeline"
                                                layout="constrained"
                                                className="h-4 w-4 sm:h-5 sm:w-5"
                                                width={96}
                                                height={96}
                                            />
                                        </div>
                                    )}
                            </div>

                            {/* <div className="flex items-center gap-3">
                                <div className="flex items-center">
                                    <span className="text-sm" style={{ color: COLORS.TEXT_PRIMARY }}>
                                        Weight Loss Program
                                    </span>
                                    <span className="mx-2" style={{ color: COLORS.TEXT_SECONDARY }}>•</span>
                                    <span className="text-sm" style={{ color: COLORS.TEXT_PRIMARY }}>
                                     
                                        {medication?.patient.name}
                                    </span>
                                    <span className="mx-2" style={{ color: COLORS.TEXT_SECONDARY }}>•</span>
                                    <span className="text-sm" style={{ color: COLORS.TEXT_PRIMARY }}>
                                        {medication?.patient.gender}
                                    </span>
                                </div>
                            </div> */}
                        </div>
                        {/* <Button className="button-outline !shadow-none -mt-1.5">View Patient Log</Button> */}
                    </div>

                    {/* Patient Information Card */}
                    <PatientCard
                        patientEmail={encounter.patient.email ?? ""}
                        patientName={encounter.patient.name ?? ""}
                        programEnrollment="Urgent Care"
                        requestDate={encounter?.createdAt || ""}
                        currentWeight=""
                        patientId={encounter.patient.id}
                    />

                    {/* Medication Refill Request Information */}
                    <div className="">
                        {/* Render batch refills with collapsible cards or single refill */}
                        {medication?.isBatch && Array.isArray(medication?.medications) ? (
                            // Batch refills - collapsible cards
                            <div className="space-y-3">
                                {medication.medications.map((med: any, index: number) => {
                                    console.log("med", med)
                                    const isExpanded = expandedRefills.has(med.refillId || `refill-${index}`);
                                    const medImageUrls = med.imageUrls || [];
                                    const medFileUrls = med.fileUrls || [];

                                    return (
                                        <div key={med.refillId || index} className="border rounded-lg overflow-hidden bg-white">
                                            {/* Collapsible Header */}
                                            <button
                                                onClick={() => toggleRefillExpansion(med.refillId || `refill-${index}`)}
                                                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                            >
                                                <div className="flex-1 text-left">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-medium text-gray-500">Medication {index + 1}</span>
                                                    </div>
                                                    <h3 className={`text-md font-medium mt-1 ${med.status === "rejected" ? "text-red-500 line-through" : "text-gray-900"}`}>{med.name}</h3>
                                                </div>
                                                <div className="ml-4 flex gap-2">
                                                    {med.status === "rejected" ? (
                                                        <Badge
                                                            variant="destructive"
                                                            className="text-white"
                                                        >
                                                            Rejected
                                                        </Badge>
                                                    ) : null}
                                                    {isExpanded ? (
                                                        <ChevronUp className="w-5 h-5 text-gray-500" />
                                                    ) : (
                                                        <ChevronDown className="w-5 h-5 text-gray-500" />
                                                    )}
                                                </div>
                                            </button>

                                            {/* Collapsible Content */}
                                            {isExpanded && (
                                                <div className="border-t bg-gray-50 px-4 pb-4">
                                                    {/* Medication Details */}
                                                    <div className="mt-4 flex flex-col items-start gap-4 rounded-lg border border-[#E6E6E6] bg-white p-2">
                                                        <h3 className="text-lg font-medium">Request Information</h3>
                                                        <RequestInfoUrgentCare
                                                            request_info={{
                                                                date_submitted: medication?.requestedDate ?? "",
                                                                last_refill: medication?.requestedDate ?? "",
                                                                days_supply: med.duration || "30",
                                                                patient: med.dosage,
                                                                medication: med.name,
                                                                refill: med.refills,
                                                            }}
                                                        />
                                                    </div>

                                                    {/* Images and Files */}
                                                    <ImagesDocsSectionWhite
                                                        imageUrls={medImageUrls}
                                                        fileUrls={medFileUrls}
                                                        onImageClick={handleImageClick}
                                                    />

                                                    {/* Reject button for this specific refill */}
                                                    <div className="mt-4 border-t pt-4">
                                                        <Button
                                                            className="w-full !rounded-[8px] !bg-[#E12121] !py-[10px] px-[16px]"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                console.log("med.refillId", med.refillId)
                                                                openRejectModal(med.refillId, {
                                                                    name: med.name,
                                                                    dosage: med.dosage,
                                                                });
                                                            }}
                                                            disabled={med.status === "rejected"}
                                                        >
                                                            {med.status === "rejected" ? "Refill Rejected" : "Reject This Refill"}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            // Single refill - non-collapsible
                            <div className="flex flex-col items-start gap-4 rounded-lg border border-[#E6E6E6] p-2">
                                <h3 className="text-lg font-medium">Request Information</h3>
                                <RequestInfoUrgentCare
                                    request_info={{
                                        date_submitted: medication?.requestedDate ?? "",
                                        last_refill: medication?.requestedDate ?? "",
                                        days_supply: "30",
                                        patient: !Array.isArray(medication?.medications)
                                            ? medication?.medications?.medicationDosage
                                            : "",
                                        medication: !Array.isArray(medication?.medications)
                                            ? medication?.medications?.medication
                                            : "",
                                        refill: !Array.isArray(medication?.medications)
                                            ? medication?.medications?.refills
                                            : 0,
                                    }}
                                />
                            </div>
                        )}
                        {/* 
                        <RefillActionButtons
                            refillId="some-refill-id"
                            onApprove={() => console.log('approved')}
                            onReject={() => console.log('rejected')}
                            isDisabled={false}
                            encounter={encounter}
                        /> */}

                        {/* <div className="sm:flex grid grid-cols-1 gap-3 mt-10 w-[90%] items-center">
                            <Button className="button-primary -ml-2 h-[44px]" onClick={() => setIsOpen(true)}>Recommend Consultation</Button>
                            <Link
                                className={cn(buttonVariants({ variant: "outline" }), "sm:w-fit w-full text-[#4272DD] rounded-[8px] h-[44px] bg-white border-2 border-[#4272DD] py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                to="/dashboard/$program/patients/patient/$id"
                                params={{
                                    id: encounter.patient.id,
                                    program
                                }}
                                search={{
                                    name: encounter.patient.name.replace(/\s+/g, ""),
                                    sessionId: encounter?.session?.session_id || ""
                                }}
                            >
                                View patient Log
                            </Link>
                            <Button
                                onClick={() => setIsOpen(true)}
                                variant="default"
                                className={cn(buttonVariants({ variant: "default" }), "rounded-[8px] !bg-[#4272DD] !text-white !hover:bg-[#3661C5] transition-colors button-primary !shadow-none !border-none h-fit")}
                                aria-label="Review medication"
                            >
                                Review medication
                            </Button>
                        </div> */}


                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-6 w-full items-center">
                            {/* Only show reject button for non-batch refills */}
                            {!medication?.isBatch && (
                                <Button
                                    className="w-[133px] !bg-[#E12121] !py-[12px] px-[16px] h-[44px] rounded"
                                    onClick={() => openRejectModal()}
                                >
                                    Reject Refill
                                </Button>
                            )}
                            <div className="flex-1">
                                <DosespotButton
                                    userDosespotId={encounter?.patient?.user_dosespot_id ?? ""}
                                    sessionId={encounter?.id ?? ""}
                                    encounterId={encounter?.encounterId ?? ""}
                                    urgent_care_onboarded={encounter.patient.urgent_care_onboarded ?? false}
                                    onSuccess={() => { }}
                                />
                            </div>
                        </div>

                        {/* Reject Modal */}
                        <Dialog open={showRejectModal} onOpenChange={closeRejectModal}>
                            <DialogContent className="rounded-[24px]">
                                <DialogHeader className="flex items-center justify-center">
                                    <DialogTitle className="text-xl font-medium">Reject Request</DialogTitle>
                                </DialogHeader>
                                <div>
                                    <p className="flex items-center mb-4 text-xl font-medium justify-center text-center">
                                        You are rejecting the refill request for {rejectingMedication
                                            ? `${rejectingMedication.name} ${rejectingMedication.dosage}`
                                            : medication?.isBatch && Array.isArray(medication?.medications)
                                                ? `${medication.medications.length} medication(s)`
                                                : !Array.isArray(medication?.medications) ? `${medication?.medications?.medication || ''} ${medication?.medications?.medicationDosage || ''}` : ''} for {medication?.userName}.
                                    </p>
                                    <label className="text-[14px] mt-4 text-[#1A202C]">
                                        <Select value={rejectReason} onValueChange={setRejectReason}>
                                            <SelectTrigger className="w-full rounded-[8px] border h-[44px] p-2 mt-2 mb-4">
                                                <SelectValue placeholder="Select a reason" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectGroup>
                                                    <SelectItem value="Cannot prescribe this medication">Cannot prescribe this medication</SelectItem>
                                                    <SelectItem value="Need more information">Need more information</SelectItem>
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                        <label className="text-[14px] mt-4 text-[#1A202C]">Please provide additional details:</label>
                                        <textarea
                                            value={rejectDetails}
                                            onChange={e => setRejectDetails(e.target.value)}
                                            maxLength={200}
                                            className="w-full rounded-[8px] border h-[164px] p-2 mt-2"
                                        />
                                    </label>
                                    {reasonError && <div className="text-red-500">{reasonError}</div>}
                                    <div className="flex justify-end gap-2 mt-4">
                                        <Button onClick={() => setShowRejectModal(false)} className="bg-transparent hover:bg-transparent border border-[#DCDCDE] rounded-[8px] text-[#202020CC]">Cancel</Button>
                                        <Button
                                            className="!bg-[#E12121] rounded-[8px] text-white"
                                            disabled={rejectRefillMutation.isPending}
                                            onClick={async () => {
                                                console.log("rejectingRefillId", rejectingRefillId)
                                                if (!rejectReason.trim()) {
                                                    setReasonError("Reason is required.");
                                                    return;
                                                }
                                                if (!rejectDetails.trim()) {
                                                    setReasonError("Additional details are required.");
                                                    return;
                                                }
                                                setReasonError("");
                                                await handleRejectRefill(rejectReason, rejectDetails, rejectingRefillId || undefined);
                                                
                                            }}
                                        >
                                            {rejectRefillMutation.isPending ? (
                                                <Loader2 className="w-4 h-4 text-white animate-spin" />
                                            ) : (
                                                "Confirm"
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>


                    </div>


                    <MedicalRefillsComponent
                        open={isOpen}
                        onOpenChange={setIsOpen}
                        refillId={medication?.id ?? ""}
                        urgent_care_onboarded={encounter.patient.urgent_care_onboarded ?? false}
                        encounterId={encounter?.encounterId ?? ""}
                        sessionId={encounter?.session?.session_id ?? ""}
                        userDosespotId={encounter?.patient?.user_dosespot_id ?? ""}
                    />

                    {/* Conditional rendering based on program type */}
                    {program === "urgent-care" && !medication?.isBatch && (
                        /* Lab Documents and Images Grid for Urgent Care - Only for single refills */
                        <ImagesDocsSection
                            imageUrls={medication?.imageUrls || []}
                            fileUrls={medication?.fileUrls || []}
                            onImageClick={handleImageClick}
                        />
                    )}
                    {/* Assigned Actions Section */}
                    {(() => {
                        const tasks = encounter?.recommendedTasks ?? [];
                        const hasAssignedActions = Array.isArray(tasks) && tasks.length > 0;
                        const isRejected = medication?.status === "rejected";

                        const humanize = (s?: string) =>
                            s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Task";

                        const getTaskTitle = (task: any) => {
                            if (task?.type === "medication_payment") return "Prescribed Medication";
                            if (task?.type === "appointment_followup") return "Pay for Appointment";
                            return humanize(task?.type);
                        };

                        return (
                            <div className="mt-6 flex w-full flex-col gap-3 rounded-[16px] border border-[#0404041A] px-4 pb-4 pt-4 text-left">
                                <h2 className="font-medium text-[#1A202C]">Assigned actions</h2>

                                {hasAssignedActions || isRejected ? (
                                    <div className="space-y-4">
                                        {/* Show rejected refill as an assigned action */}
                                        {isRejected && medication?.rejectionReason && (
                                            <div className="bg-[#F7F8FA] rounded-[8px] p-4">
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                                                            <X className="w-3 h-3 text-white" />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-medium text-red-600">
                                                                Rejected Refill Request
                                                            </h3>
                                                            <p className="text-sm text-[#6B7280]">
                                                                Rejected: {formatDate(medication?.requestedDate)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="bg-white rounded-md p-2 space-y-2">
                                                    <h4 className="font-medium text-[#1A202C]">
                                                        {medication?.rejectionReason?.reason || "Rejection Reason"}
                                                    </h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#6B7280]">
                                                        {medication?.isBatch && Array.isArray(medication?.medications) ? (
                                                            // Show all medications for batch
                                                            medication.medications.map((med: any, idx: number) => (
                                                                <div key={idx} className="md:col-span-2 border-b pb-2 mb-2">
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        <div>
                                                                            <span className="font-medium">Medication: </span>
                                                                            <span className="line-through text-red-600">
                                                                                {med?.name ?? "-"}
                                                                            </span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Dosage: </span>
                                                                            {med?.dosage ?? "-"}
                                                                        </div>
                                                                        <div>

                                                                            <span className="font-medium">Refills: </span>
                                                                            {med?.refills ?? "-"}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <>
                                                                <div>
                                                                    <span className="font-medium">Medication: </span>
                                                                    <span className="line-through text-red-600">
                                                                        {!Array.isArray(medication?.medications) ? medication?.medications?.medication ?? "-" : "-"}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Dosage: </span>
                                                                    {!Array.isArray(medication?.medications) ? medication?.medications?.medicationDosage ?? "-" : "-"}
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Refills: </span>
                                                                    {!Array.isArray(medication?.medications) ? medication?.medications?.refills ?? "-" : "-"}
                                                                </div>
                                                            </>
                                                        )}
                                                        <div className="md:col-span-2">
                                                            <span className="font-medium">Rejection Details: </span>
                                                            <span>{medication?.rejectionReason?.details ?? "-"}</span>
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Request Date: </span>
                                                            {formatDate(medication?.requestedDate)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Show recommended tasks */}
                                        {tasks.map((task: any, index: number) => {
                                            const isMedicationTask = task?.type === "medication_payment";
                                            const meds = task?.prescription?.medication ?? [];
                                            const isTaskCompleted = task?.status === "completed";
                                            const isTaskPending = task?.status === "pending";
                                            const hasPrescription = task?.prescription && meds.length > 0;
                                            // Only show green if task is completed (not pending) and has prescription for medication tasks
                                            const showGreen = isMedicationTask
                                                ? (hasPrescription && !isTaskPending && isTaskCompleted)
                                                : isTaskCompleted;

                                            return (
                                                <div
                                                    key={task?.task_id || index}
                                                    className="rounded-[8px] bg-[#F7F8FA] p-4"
                                                >
                                                    <div className="mb-3 flex items-start justify-between">
                                                        <div className="flex items-center gap-3">
                                                            {showGreen ? (
                                                                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500">
                                                                    <Check className="h-3 w-3 text-white" />
                                                                </div>
                                                            ) : (
                                                                <Circle className="h-5 w-5 text-blue-500" />
                                                            )}
                                                            <div>
                                                                <h3
                                                                    className={cn(
                                                                        "font-medium",
                                                                        showGreen ? "text-green-600 line-through" : "text-[#1A202C]"
                                                                    )}
                                                                >
                                                                    {getTaskTitle(task)}
                                                                </h3>
                                                                <p className="text-sm text-[#6B7280]">
                                                                    Assigned: {formatDate(task?.created_at)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Medication details */}
                                                    {isMedicationTask && Array.isArray(meds) && meds.length > 0 && (
                                                        <div className="space-y-3">
                                                            {meds.map((m: any, i: number) => (
                                                                <div
                                                                    key={`${m?.name || "med"}-${i}`}
                                                                    className="space-y-2 rounded-md bg-white p-2"
                                                                >
                                                                    <h4 className="font-medium text-[#1A202C]">{m?.name || "-"}</h4>
                                                                    <div className="grid grid-cols-1 gap-4 text-sm text-[#6B7280] md:grid-cols-2">
                                                                        <div>
                                                                            <span className="font-medium">Dosage: </span>
                                                                            {m?.dosage ?? "-"}
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Duration: </span>
                                                                            {typeof m?.duration === "number"
                                                                                ? `${m.duration} days`
                                                                                : (m?.duration ?? "-")}
                                                                        </div>
                                                                        <div className="md:col-span-2">
                                                                            <span className="font-medium">Instructions: </span>
                                                                            {Array.isArray(m?.instructions) ? (
                                                                                <ul className="ml-5 list-disc">
                                                                                    {m.instructions.map((ins: string, idx: number) => (
                                                                                        <li key={idx}>{ins}</li>
                                                                                    ))}
                                                                                </ul>
                                                                            ) : (
                                                                                <span>{m?.instructions ?? "-"}</span>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Refills: </span>
                                                                            {m?.refills ?? "-"}
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Effective date: </span>
                                                                            {formatDate(m?.effective_date)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Non-medication task details */}
                                                    {!isMedicationTask && (
                                                        <div className="space-y-2 rounded-md bg-white p-2 text-sm text-[#6B7280]">
                                                            <div>
                                                                <span className="font-medium">Price: </span>
                                                                <span>{task?.cost ?? "-"}</span>
                                                            </div>
                                                            {task?.instructions && (
                                                                <div>
                                                                    <span className="font-medium">Instructions: </span>
                                                                    <span>{task.instructions}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center rounded-[8px] bg-[#F7F8FA] px-6 py-12 text-center">
                                        <p className="mb-1 font-medium text-[#1A202C]">No assigned actions</p>
                                        <p className="text-sm text-[#6B7280]">
                                            When actions are assigned, they will appear here.
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* Image Modal */}
            {selectedImage && (
                <ImageModal
                    isOpen={!!selectedImage}
                    onClose={handleCloseImageModal}
                    imageUrl={selectedImage.url}
                    imageIndex={selectedImage.index}
                    totalImages={medication?.imageUrls?.length || 0}
                />
            )}

            {/* Prescription Modal for weight-loss */}
            {openPrescriptionModal && program === "weight-loss" && encounter?.patient?.id && (
                <PrescriptionModal
                    open={openPrescriptionModal}
                    patientId={encounter.patient.id}
                    encounterId={encounter?.encounterId}
                    onOpenChange={setOpenPrescriptionModal}
                />
            )}
        </>
    );
};
